import i from 'i18next'
import { motion } from 'framer-motion'
import useTranslations, { getTranslations } from 'lib/hooks/use-translations'
import { getProductInfo, getProducts, getWeightedAverage } from 'lib/hooks/use-products'
import { getMenu, getMenuCollections } from 'lib/hooks/use-collections'
import Layout from 'components/Layout'
import Title from 'components/Title'
import { maxWidth } from 'constants/styles'
import TextImageHalf from 'components/sections/TextImageHalf'
import Footer from 'components/sections/Footer'
import Products from 'components/sections/Products'
import Image from 'components/Image'
import Link from 'components/Link'
import { H1 } from 'components/sections/Header'
import { headers as ease } from 'constants/easings'
import Rating from 'components/product/Rating'

export async function getStaticProps() {
	const translations = await getTranslations({ page: 'home' })
	const nav = await getMenu()
	const collections = await getMenuCollections()
	const favorites = await getProducts( 'hidden-home-favorites' )
	const routines = await getProducts( 'hidden-home-routines' )
	const { num: reviews, average } = getWeightedAverage( await getProductInfo() )

	return {
		props: { translations, nav, collections, favorites, routines, reviews, average },
		revalidate: 3600,
	}
}

// eslint-disable-next-line max-len
export default function Home({ loaded, translations, nav, collections, favorites, routines, reviews, average }) {
	useTranslations( translations )

	return (
		<Layout loaded={loaded} nav={nav} collections={collections}>
			<Title page="home" />

			<header className="container">
				<H1 title={i.t( 'home.hero.text' )} lines={4} />
				<motion.div
					style={{ marginTop: 34 }}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 1, delay: 1.3, ease }}
				>
					<Link href="/reviews">
						<a>
							<Rating big count={reviews} rating={average} />
						</a>
					</Link>
				</motion.div>
				<motion.div
					style={{ marginTop: 34 }}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 1, delay: .75, ease }}
				>
					<Link href="/products/zon">
						<motion.button
							className="button"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: .5, delay: 1.2 }}
						>
							{i.t( 'home.hero.cta.sun' )}
						</motion.button>
					</Link>
					{/* <motion.div
						className="underline"
						style={{ transformOrigin: '0% 0%' }}
						initial={{ scaleX: 0 }}
						animate={{ scaleX: 1 }}
						transition={{ duration: .01, delay: 1.2, ease }}
					/> */}
				</motion.div>
			</header>

			<Image1 />

			<Products
				title={i.t( 'home.favorites' )}
				products={favorites}
			/>

			<TextImageHalf
				text={i.t( 'home.story', { postProcess: 'markdown-jsx' })}
				image={i.t( 'home.story.image' ) }
			/>

			<Products
				title={i.t( 'home.routines' )}
				products={routines}
			/>

			<TextImageHalf
				title={i.t( 'home.ingredients.title' )}
				text={i.t( 'home.ingredients.text', { postProcess: 'markdown-jsx' })}
				image={i.t( 'home.ingredients.image' ) }
			/>

			<Footer />

			<style jsx>{`
				header {
					height: calc( 100vh - 53px - 120px );
					padding-bottom: 6vh;
					display: flex;
					flex-direction: column;
					justify-content: center;
					align-items: flex-start;
				}
				@media ( max-height: 800px ) {
					header {
						padding-left: 30px;
					}
				}
				header :global( h1 ) {
					margin-top: 0;
				}
				@media ( min-height: 800px ) {
					header :global( h1 ) {
						margin-top: 0;
						font-size: 51px;
						line-height: 57px;
					}
				}
				header a {
					text-decoration: none;
					font-size: 22px;
  				font-weight: 500;
				}
				header :global( .underline ) {
					margin-top: 6px;
					height: 2px;
					background-color: black;
					transition: transform cubic-bezier( 0.62, 0.05, 0.01, 0.98 ) 1s;
				}
				header a:hover + :global( .underline ) {
					transform: scaleX( 0 ) !important;
					transform-origin: top right !important;
				}
				@media ( max-width: 900px ) {
					#packaging + :global( section.right > div ) {
						flex-wrap: wrap;
					}
					#packaging + :global( section.right > div picture ) {
						margin-bottom: 50px;
					}
				}
				:global( .image-1 ) {
					position: absolute;
					top: calc( 200px );
					right: 0;
					width: 46%;
					height: calc( 85vh - 150px + 15vh );
				}
				:global( .image-2 ) {
					position: relative;
					width: calc( ( 100% - ${maxWidth}px ) / 2 + ${maxWidth}px );
					height: 100vh;
				}
				@media ( max-width: 1300px ) {
					:global( .image-2 ) {
						width: 95vw;
					}
				}
				@media ( max-width: 900px ) {
					header {
						height: auto;
						margin-top: 20px;
						margin-bottom: 50px;
					}
					header :global( h1 ) {
						font-size: clamp(20px,10vw,35px);
						line-height: 1.29;
					}
					header :global(button) {
						height: 70px;
						padding-inline: clamp(20px,8vw,56px);
					}
					:global( .image-1 ) {
						position: static;
						width: 100%;
					}
					:global( .image-2 ) {
						display: none;
					}
				}
			`}</style>
		</Layout>
	)
}

const Image1 = () => {
	// const { scrollY } = useScrollerMotion()
	// const { width, height } = useWindowSize()
	// const yTo = width > 900 ? '40%' : '0'
	// const y = useTransform( scrollY, [ 0, height * 2.5 ], [ '0', yTo ] )

	return (
		<motion.picture
			className="image-1"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			// exit={{ y: 100, opacity: 0 }}
			transition={{ duration: 1.5, delay: .25, ease }}
			// style={{ y }}
		>
			<Image
				src={i.t( 'home.images.1' )}
				sizes={`( max-width: 1300px ) 100vw, ${Math.ceil( 46 * 1.15 )}vw`}
				loading="lazy"
				style={{ height: '115%' }}
				initial={{ y: 0 }}
				animate={{ y: '-10%' }}
				transition={{ duration: 1.5, delay: .15, ease }}
			/>
		</motion.picture>
	)
}

// const Image2 = () => {
// 	const { scrollY } = useScrollerMotion()
// 	const { height } = useWindowSize()
// 	const y = useTransform( scrollY, [ 0, height * 2 ], [ '0%', `-${parallax}%` ] )

// 	return (
// 		<picture className="image-2">
// 			<Image
// 				src={i.t( 'home.images.2' )}
// 				style={{ y, scale: 1.05 }}
// 			/>
// 		</picture>
// 	)
// }
